/**
 * Turning a wire payload back into the shape the platform contract declares.
 *
 * GraphQL constrains enum *names* and not the values they stand for, so
 * `Exported and Locked` travels as `EXPORTED_AND_LOCKED` and `client-admin` as
 * `CLIENT_ADMIN`. That translation has two sides. The server serialises a
 * domain value to a name, which `api/graphql/scalars.ts` wires into the
 * executable schema. The client reads a name back and has to answer with the
 * value its own contract declares, which is what {@link decodePayload} does.
 *
 * It lives here, in the contract layer, rather than beside the server half.
 * Both sides need it, and the copy that drifts is the one that silently hands a
 * caller `CLIENT_ADMIN` where every consumer of `ProgrammePlatformClient`
 * expects `client-admin`: nothing throws, the value is simply never equal to
 * anything it is compared against.
 *
 * The other half is absence. GraphQL answers a selected nullable field with an
 * explicit `null`; the domain omits the key. `toEqual` tells those apart and so
 * does every consumer that reads `"x" in value`, so a typed payload has to be
 * brought back to the domain's own representation before anything sees it.
 *
 * Nulls are dropped by default and kept only where the domain genuinely means
 * one. That list is short and it is written down here, because it is the one
 * piece of this that no schema can tell you: `Programme.nfqLevel` is `null` for
 * a programme whose level is unspecified, and the portfolio filter offers
 * "unspecified" as a choice by testing exactly that. `client-differential.test.ts`
 * is what keeps the list right, comparing every read against the domain's own
 * answer field by field, so a field wrongly dropped or wrongly kept fails there
 * by name.
 *
 * @module platform/graphql-enums
 */

import { FIELD_TYPES } from "./graphql-enum-fields.generated";
import { enumValues } from "./graphql-enum-values";

export { enumValues };

/**
 * Fields that carry an explicit `null` rather than being dropped.
 *
 * Addressed as `Type.field`. Everything not named here loses its nulls, because
 * the domain expresses "no value" as an absent key almost everywhere and a
 * `null` reaching a consumer that checks for absence is a silent wrong answer.
 *
 * Each entry is a place the domain draws the distinction on purpose.
 */
const EXPLICIT_NULLS = new Set([
  // A programme whose NFQ level is unspecified, which the portfolio filter
  // offers as its own choice by testing `nfqLevel === null`.
  "Programme.nfqLevel",
  "ModuleWorkspace.nfqLevel",
  // The Portfolio's own level filter builds its options the same way, so an
  // absent key here would put `undefined` in the dropdown as a level.
  "PortfolioProgrammeRevision.nfqLevel",
]);

/**
 * The object type a root field answers with, or nothing when it answers `JSON`.
 *
 * Read from the generated map rather than named at each call site. Sixty-two
 * operations each passing their own payload type is sixty-two chances to pass
 * the wrong one, and a wrong one is silent: it decodes the fields that happen
 * to collide and leaves the rest.
 *
 * @param field - The root field the document selected.
 * @returns Its payload type, or `undefined` for an untyped payload.
 */
export function payloadTypeOf(field: string): string | undefined {
  return FIELD_TYPES.Query?.[field] ?? FIELD_TYPES.Mutation?.[field];
}

/**
 * Translate one GraphQL enum name into its domain value.
 *
 * Anything that is not a string is handed back untouched: a field the map names
 * may still arrive as a number or an object when the schema declares it more
 * loosely than the map does, and guessing at those is how a passthrough becomes
 * a corruption.
 *
 * @param names - The enum's name-to-value map.
 * @param held - The value as it arrived on the wire.
 * @returns The domain value, or `held` when there is nothing to translate.
 */
function domainValue(names: Record<string, string>, held: unknown): unknown {
  return typeof held === "string" ? (names[held] ?? held) : held;
}

/**
 * Turn a domain value into the enum name the schema takes as input.
 *
 * A value the enum does not hold goes through unchanged, so schema validation
 * refuses it by name rather than this adapter guessing at it.
 *
 * @param enumType - The GraphQL enum the input field is typed with.
 * @param value - The domain value.
 * @returns The enum name, or `value` when the enum has no name for it.
 */
export function encodeEnumName(enumType: string, value: string): string {
  const names = Object.entries(enumValues[enumType] ?? {});
  return names.find(([, domain]) => domain === value)?.[0] ?? value;
}

/**
 * Turn a wire payload into the shape the platform contract declares.
 *
 * Two things, in one walk over the schema's own field types. Every GraphQL enum
 * name becomes the domain value it stands for, and every `null` that the domain
 * would have expressed as an absent key is dropped.
 *
 * A type the map does not name is passed through whole: `JSON` payloads and the
 * open-keyed documents inside them must survive this untouched, keys and all.
 *
 * @param typeName - The object type the value was selected from.
 * @param value - The payload, or a list of them, as it arrived on the wire.
 * @returns The payload with enum names replaced by domain values.
 */
export function decodePayload<T>(typeName: string, value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry: unknown) => decodePayload(typeName, entry)) as unknown as T;
  }
  if (value === null || typeof value !== "object") {
    return value;
  }

  const fields = FIELD_TYPES[typeName];
  if (!fields) {
    return value;
  }

  const decoded: Record<string, unknown> = Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      ([field, held]) => held !== null || EXPLICIT_NULLS.has(`${typeName}.${field}`),
    ),
  );
  Object.entries(fields).forEach(([field, fieldType]) => {
    const held = decoded[field];
    if (held === undefined || held === null) {
      return;
    }
    const names = enumValues[fieldType];
    if (!names) {
      decoded[field] = decodePayload(fieldType, held);
      return;
    }
    decoded[field] = Array.isArray(held)
      ? held.map((entry: unknown) => domainValue(names, entry))
      : domainValue(names, held);
  });
  return decoded as T;
}
